import { Component, inject, signal, effect, SecurityContext, ViewChild, ElementRef, AfterViewInit, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { marked } from 'marked';
import { apiFetch } from './api-client';
import { readJsonSse } from './sse-stream';

type ChatMessage = {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number; // epoch ms
  attachments?: { src: string; name?: string }[];
  citationSources?: CitationSourceMap;
  error?: boolean;
};

type CitationSource = { url: string; title: string };
type CitationSourceMap = Record<string, CitationSource>;

// Progress step type for real-time UI updates
type ProgressStep = {
  step: string;
  status: 'pending' | 'running' | 'done' | 'error';
  message: string;
  expanded?: boolean;  // For expandable plan details
  detail?: { 
    current?: number; 
    total?: number; 
    sources?: number;
    iteration?: number;
    maxIterations?: number;
    pagesProcessed?: number;
    totalPages?: number;
    plan?: string;           // Raw plan
    planMarkdown?: string;   // Markdown formatted plan
    planSummary?: string;    // Short summary
    decision?: string;       // LLM decision
    willUseTool?: boolean;
    toolName?: string;
    query?: string;          // Search query if any
    renderAsMarkdown?: boolean;
  };
};

type ChatStreamEvent = {
  type?: 'progress' | 'sources' | 'content' | 'revert' | 'error' | 'done';
  step?: string;
  status?: ProgressStep['status'];
  message?: string;
  error?: string;
  content?: string;
  done?: boolean;
  detail?: ProgressStep['detail'];
  sources?: Array<{ id: number; url: string; title?: string }>;
};

// Step display names for UI - Only steps actually used in the flow
// Flow: pii_check -> classify -> agent loop (agent_planning/search/fetch_pages/synthesize) -> generate
const STEP_LABELS_HEALTHCARE: Record<string, string> = {
  request: 'Starting',
  resolve_query: 'Understanding',
  pii_check: 'Privacy & Security Check',
  classify: 'Understanding',
  agent_planning: 'Reasoning',
  agent_analysis: 'Thinking',
  search: 'Reflecting',
  fetch_pages: 'Considering',
  summarize: 'Reviewing Sources',
  synthesize: 'Connecting Ideas',
  generate: 'Finalizing',
  complete: 'Complete',
  error: 'Processing',
};

// Step display names for UI - Same for all modes
const STEP_LABELS_GENERAL: Record<string, string> = {
  request: 'Starting',
  resolve_query: 'Understanding',
  pii_check: 'Privacy & Security Check',
  classify: 'Understanding',
  agent_planning: 'Reasoning',
  generate: 'Finalizing',
  error: 'Processing',
};

// Running messages - Only for actually used steps
const RUNNING_MESSAGES: Record<string, string[]> = {
  request: [
    'Starting your request...',
  ],
  pii_check: [
    'Ensuring your privacy and data security...',
    'Protecting your sensitive information...',
    'Checking for your safety...',
  ],
  classify: [
    'Understanding your question...',
    'Analyzing the context...',
  ],
  agent_planning: [
    'Reasoning through the problem...',
    'Considering different angles...',
    'Thinking deeply...',
    'Working through the logic...',
  ],
  agent_analysis: [
    'Processing insights...',
  ],
  search: [
    'Reflecting on this...',
    'Thinking about related concepts...',
  ],
  fetch_pages: [
    'Considering the details...',
    'Examining the nuances...',
  ],
  summarize: [
    'Reviewing the most relevant sources...',
  ],
  synthesize: [
    'Connecting the ideas...',
    'Putting it together...',
  ],
  generate: [
    'Finalizing your response...',
  ],
  complete: [
    'Finalizing...',
  ],
  error: [
    'Processing...',
  ],
};

// Done state messages - empty (icon shows checkmark already)
const DONE_MESSAGES: Record<string, string[]> = {
  request: [''],
  pii_check: [''],
  classify: [''],
  agent_planning: [''],
  agent_analysis: [''],
  search: [''],
  fetch_pages: [''],
  summarize: [''],
  synthesize: [''],
  generate: [''],
  complete: [''],
  error: [''],
};

// Helper function to get random message from array
function getRandomMessage(messages: string[]): string {
  return messages[Math.floor(Math.random() * messages.length)];
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat.component.html',
  styles: [
    `:host{display:block;height:100%;}`,
    `
    .bubble{ max-width: 90%; border-radius: 16px; padding: 10px 12px; }
    .bubble-assistant{ 
      background: #f3f4f6; 
      border: 1px solid #f3f4f6; 
      font-size: 14px;
      color: #1f2937; 
      width: 90%; /* Always full width */
      max-width: 90%;
    }
    
    /* Smooth chat scroll */
    .chat-scroll {
      scroll-behavior: smooth;
    }
    .bubble .timestamp{ font-size: 0.75rem; line-height: 1; }
    .bubble-assistant .timestamp{ opacity: 0.75; }
    .copy-btn{ top: -10px; right: -5px; border-radius: 4px; z-index: 2;
    padding: 2px 2px 2px; border: 1px solid #e5e7eb !important; color: #6c757d;
    background-color: #fff; font-size: 12px;line-height: 0px; display: inline-block; transform: translateZ(0); transform-origin: center; transition: transform 0.15s cubic-bezier(.4, 0, .2, 1);  will-change: transform; }
    .copy-btn:hover {transform: scale(1.15) translateZ(0); }
    /*.copy-btn, .copy-btn:focus{ background: #ffffff !important; border: none !important; box-shadow: none !important; }*/
    .copy-btn i{ color: #6c757d; font-size: 14px; transition: transform .15s ease; }
    .copy-btn:hover i{ transform: scale(1.15); color: #374151; }
    .send-icon{ display:inline-block; transform: rotate(-45deg) translateY(1px); }
    .sendbtn{ width: 36px; height: 36px; border-radius: 50% !important; display: inline-flex; align-items: center; justify-content: center; transition: transform .12s ease; background-color:#e5e7eb; color:#6b727e; border-radius: 8px; }
    .sendbtn:not(:disabled):hover{ transform: scale(1.08); }
    .sendbtn.btn-primary:hover,
    .sendbtn.btn-primary:focus,
    .sendbtn.btn-primary:active{ background-color: var(--bs-primary) !important; border-color: var(--bs-primary) !important; color: #fff !important; box-shadow: none !important; }
    .sendbtn i{ font-size: 16px; line-height: 0; margin: 2px 0 0 0; }
    .sendbtn .spinner-border{ width: 1rem; height: 1rem; }
    .sendbtn-gradient{ background-image: linear-gradient(135deg, #6b46c1, #a855f7 50%, #f97316); color: #fff; border: none; }
    .sendbtn-gradient:hover, .sendbtn-gradient:focus, .sendbtn-gradient:active{ background-image: linear-gradient(135deg, #6b46c1, #a855f7 50%, #f97316) !important; color: #fff !important; border: none !important; box-shadow: none !important; }
    /* Textarea input styling */
    .typing-text {
      min-height: 52px;
      max-height: 120px;
      resize: none;
      border-radius: 16px !important;
      padding: 12px 16px;
      font-size: 14px;
      line-height: 1.4;
    }
    .typing-text::placeholder {
      color: #9ca3af;
      line-height: 1.4;
    }
    /* Thin scrollbar styling for messages area */
    /*.chat-scroll{ scrollbar-width: thin; scrollbar-color: rgba(108, 15, 230, 1) transparent; }*/
    .chat-scroll::-webkit-scrollbar{ width: 5px; height: 5px; }
    .chat-scroll::-webkit-scrollbar-track{ background: transparent; }
    .chat-scroll::-webkit-scrollbar-thumb{ background: linear-gradient(135deg,#6b46c1,#a855f7); border-radius: 3px; }
    .attachments{ display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .5rem; }
    .attachments .thumb-link{ position: relative; display: block; width: 100%; height: 120px; background: #000; border-radius: 6px; overflow: hidden; border: 1px solid #8f6cd3; }
    .attachments .thumb{ width: 100%; height: 100%; object-fit: contain; border: 0; background: transparent; }
    .drop-overlay{ position: absolute; inset: 0; background: rgba(255,255,255,0.6); backdrop-filter: blur(4px); display:flex; align-items:center; justify-content:center; z-index: 1000; }
    .drop-box{ border: 2px dashed rgba(108, 15, 230, 1); color: #1a212f; background: rgba(255,255,255,0.85); padding: 16px 24px; border-radius: 12px; text-align:center; font-weight: 500; }
    /* Unified "working" indicator: one compact pill, not a stacked card + separate line */
    .status-pill {
      display: inline-flex;
      align-items: center;
      gap: 9px;
      max-width: 100%;
      padding: 7px 14px 7px 10px;
      border-radius: 999px;
      background: linear-gradient(135deg, rgba(107, 70, 193, 0.07), rgba(168, 85, 247, 0.1));
      border: 1px solid rgba(107, 70, 193, 0.12);
    }

    .status-pill-spinner {
      flex-shrink: 0;
      width: 15px;
      height: 15px;
      border-radius: 50%;
      border: 2px solid rgba(107, 70, 193, 0.18);
      border-top-color: #8b5cf6;
      animation: status-pill-spin 0.75s linear infinite;
    }

    @keyframes status-pill-spin {
      to { transform: rotate(360deg); }
    }

    .status-pill-text {
      display: flex;
      align-items: baseline;
      gap: 8px;
      min-width: 0;
      overflow: hidden;
    }

    .status-pill-label {
      font-weight: 600;
      font-size: 13px;
      color: #6b46c1;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .status-pill-message {
      font-size: 12.5px;
      color: #7c7f8a;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }

    /* Old mobile progress CSS removed - now using shimmer container only */
    
    /* Right align badges and counts */
    .iteration-badge {
      margin-left: auto;
      text-align: right;
    }
    .step-meta {
      justify-content: flex-end;
    }
    .step-meta .progress-bar-container {
      flex: 1;
    }
    .step-meta .progress-count,
    .step-meta .iteration-badge {
      flex-shrink: 0;
      text-align: right;
    }
    
    /* Faded messages outside context window */
    .message-faded {
      position: relative;
      transition: opacity 0.3s ease;
    }
    .message-faded::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to bottom, rgba(255,255,255,0.3), rgba(255,255,255,0.1));
      pointer-events: none;
      border-radius: inherit;
    }
    
    /* Advanced settings panel */
    .form-range::-webkit-slider-thumb {
      background: linear-gradient(135deg, #6b46c1, #a855f7);
    }
    .form-range::-moz-range-thumb {
      background: linear-gradient(135deg, #6b46c1, #a855f7);
    }
    
    /* Error box styling */
    .error-box {
      background-color: #fee2e2;
      border: 1px solid #fca5a5;
      border-radius: 8px;
      padding: 12px 16px;
      color: #dc2626;
      font-size: 14px;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .error-box i {
      color: #ef4444;
    }
    
    /* Progress bar styling - full width with gradient theme */
    .progress-bar-container {
      flex: 1;
      height: 8px;
      background-color: #e5e7eb;
      border-radius: 4px;
      overflow: hidden;
      position: relative;
      min-width: 100px;
    }
    .progress-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #6b46c1, #a855f7, #c084fc);
      border-radius: 4px;
      transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 0 8px rgba(139, 92, 246, 0.4);
      position: relative;
    }
    .progress-bar-fill::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
      animation: shimmerBar 1.5s infinite;
    }
    @keyframes shimmerBar {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    .progress-count {
      font-size: 11px;
      color: #6b46c1;
      margin-left: auto;
      font-weight: 600;
      white-space: nowrap;
      text-align: right;
    }
    .iteration-badge {
      font-size: 9px;
      background: linear-gradient(135deg, #6b46c1, #a855f7);
      color: white;
      padding: 2px 8px;
      border-radius: 10px;
      margin-left: auto;
      font-weight: 500;
      white-space: nowrap;
    }
    /* Plan detail expanded view */
    .plan-detail-expanded {
      margin-top: 8px;
      padding: 10px 12px;
      background: rgba(107, 70, 193, 0.05);
      border-left: 3px solid #6b46c1;
      border-radius: 0 8px 8px 0;
      font-size: 12px;
      line-height: 1.6;
      width: 100%;
      max-width: 100%;
      overflow: visible;
    }
    .plan-markdown-content {
      color: #374151;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    .plan-markdown-content p {
      margin: 0 0 8px 0;
      white-space: pre-wrap;
    }
    .plan-markdown-content strong {
      color: #6b46c1;
      font-weight: 600;
    }
    .plan-markdown-content ul, .plan-markdown-content ol {
      margin: 4px 0 8px 0;
      padding-left: 16px;
      list-style-position: outside;
    }
    .plan-markdown-content li {
      margin-bottom: 4px;
      white-space: normal;
    }
    .plan-query-info {
      margin-top: 10px;
      padding-top: 8px;
      border-top: 1px solid rgba(107, 70, 193, 0.15);
      display: flex;
      align-items: flex-start;
      gap: 6px;
      flex-wrap: wrap;
    }
    .plan-query-info .query-label {
      font-weight: 500;
      color: #6b46c1;
      font-size: 11px;
      flex-shrink: 0;
    }
    .plan-query-info .query-text {
      font-style: italic;
      color: #4b5563;
      font-size: 11px;
      word-break: break-word;
    }
    /* Mode tab button styling now lives in the segmented-control block further down
       (.mode-tabs-bar / .mode-track / .mode-btn) - kept together with its markup. */

    /* Mobile Responsiveness */
    @media (max-width: 768px) {
      /* Adjust bubbles for smaller screens */
      .bubble { max-width: 95%; padding: 8px 12px; font-size: 14px; }
      
      /* Compact progress steps */
      .progress-step { flex-wrap: wrap; gap: 4px; padding: 6px 0; }
      .progress-step .step-icon { width: 16px; height: 16px; }
      .progress-step .step-label { min-width: auto; margin-right: 8px; font-size: 12px; }
      .progress-step .step-message { width: 100%; margin-left: 24px; font-size: 11px; margin-top: -2px; }
      
      /* Adjust input area */
      .sendbtn { width: 32px; height: 32px; }
      textarea { font-size: 14px; min-height: 40px !important; }
      
      /* Full width progress bars on mobile */
      .step-meta { 
        flex-wrap: wrap; 
        display: flex !important;
      }
      .progress-bar-container { 
        flex: 1; 
        min-width: 80px; 
        margin-left: 0; 
        margin-top: 2px; 
        height: 6px;
        display: block !important;
        background-color: #e5e7eb !important;
      }
      .progress-bar-fill {
        display: block !important;
        height: 100% !important;
        background: linear-gradient(90deg, #6b46c1, #a855f7, #c084fc) !important;
      }
      .progress-count { margin-left: 8px; font-size: 10px; }
      
      /* Adjust spacing */
      .chat-scroll { padding-left: 10px !important; padding-right: 10px !important; }
      
      /* Smaller suggestions on mobile */
      .action-btn { font-size: 11px !important; padding: 4px 10px !important; }
    }

    /* Suggestions Animation */
    .suggestions-wrapper {
      overflow: hidden;
      max-height: 200px; /* Enough to fit rows */
      transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: 1;
      transform: translateY(0);
      transform-origin: top;
    }
    .suggestions-wrapper.collapsed {
      max-height: 0;
      opacity: 0;
      margin-top: 0 !important;
      transform: translateY(-5px);
      pointer-events: none;
    }
    
    /* Footer controls styling - compact 35% size */
    .footer-controls {
      font-size: 8px;
    }
    .footer-toggle-btn {
      color: #9ca3af !important;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 2px 4px !important;
      border-radius: 8px;
      font-size: 8px !important;
    }
    .footer-toggle-btn:hover, .footer-toggle-btn:focus {
      color: #6b46c1;
      background-color: #f3f4f6;
      text-decoration: none;
    }
    .footer-toggle-btn.active {
      color: #6b46c1;
      background-color: #f3f4f6;
    }
    
    /* Suggestion buttons - compact size with white hover text */
    .action-btn {
      font-size: 8px !important;
      padding: 2px 6px !important;
      border-color: #d1d5db;
      color: #6b7280;
    }
    .action-btn:hover {
      border-color: #6b46c1;
      color: #fff !important;
      background: linear-gradient(135deg, #6b46c1, #a855f7 50%, #f97316) !important;
    }
    
    /* Extra-small device optimizations */
    @media (max-width: 480px) {
      .mode-btn { font-size: 12px !important; padding: 4px 12px !important; }
      .bubble { max-width: 98%; padding: 8px 10px; }
      .footer-toggle-btn { font-size: 10px; }
    }
    
    /* Citation badge - Purple theme with white text */
    .cite-badge {
      display: inline-block;
      background: linear-gradient(135deg, #6b46c1, #a855f7);
      color: #fff !important;
      text-decoration: none !important;
      font-size: 12px;
      font-weight: 500;
      padding: 3px 10px;
      border-radius: 4px;
      margin: 0 3px;
      vertical-align: middle;
      transition: all 0.15s ease;
      cursor: pointer;
      border: none;
      white-space: nowrap;
      line-height: 1.4;
    }
    .cite-badge:hover {
      background: linear-gradient(135deg, #5b38a8, #9333ea);
      color: #fff !important;
      text-decoration: none !important;
      box-shadow: 0 2px 8px rgba(107, 70, 193, 0.4);
      transform: translateY(-1px);
    }
    
    /* AI Follow-up Suggestions */
    .follow-up-container {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid rgba(107, 70, 193, 0.15);
    }
    .follow-up-label {
      font-size: 11px;
      color: #6b46c1;
      font-weight: 600;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .follow-up-label i {
      font-size: 12px;
    }
    .follow-up-questions {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .follow-up-btn {
      background: linear-gradient(135deg, rgba(107, 70, 193, 0.08), rgba(168, 85, 247, 0.12));
      border: 1px solid rgba(107, 70, 193, 0.25);
      border-radius: 12px;
      padding: 10px 14px;
      font-size: 13px;
      color: #374151;
      text-align: left;
      cursor: pointer;
      transition: all 0.2s ease;
      line-height: 1.4;
      display: flex;
      align-items: flex-start;
      gap: 10px;
    }
    .follow-up-btn:hover {
      background: linear-gradient(135deg, rgba(107, 70, 193, 0.15), rgba(168, 85, 247, 0.2));
      border-color: #6b46c1;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(107, 70, 193, 0.15);
    }
    .follow-up-btn:active {
      transform: translateY(0);
    }
    .follow-up-btn .question-icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      background: linear-gradient(135deg, #6b46c1, #a855f7);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 11px;
      font-weight: 600;
    }
    .follow-up-btn .question-text {
      flex: 1;
    }
    .follow-up-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px;
      color: #6b46c1;
      font-size: 12px;
    }
    .follow-up-loading .spinner-border {
      width: 14px;
      height: 14px;
      border-width: 2px;
    }
    
    /* Mobile adjustments for follow-up */
    @media (max-width: 768px) {
      .follow-up-btn {
        font-size: 12px;
        padding: 8px 12px;
      }
      .follow-up-btn .question-icon {
        width: 18px;
        height: 18px;
        font-size: 10px;
      }
    }

    /* ---- Mode tabs: segmented control ---- */
    .mode-tabs-bar {
      display: flex;
      justify-content: center;
      padding: 10px 12px;
      background: #fff;
      border-bottom: 1px solid #f1f1f3;
    }
    .mode-track {
      display: inline-flex;
      gap: 2px;
      padding: 3px;
      background: #f3f4f6;
      border-radius: 12px;
    }
    .mode-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border: none;
      background: transparent;
      color: #6b7280;
      font-size: 12.5px;
      font-weight: 600;
      padding: 6px 14px;
      border-radius: 9px;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .mode-btn:hover:not(.mode-btn-active) { color: #374151; }
    .mode-btn-active {
      background: #fff !important;
      color: #6b46c1 !important;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.06);
    }

    /* ---- Empty-state hero ---- */
    .hero {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: 28px 12px 8px;
      max-width: 480px;
      margin: 0 auto;
    }
    .hero-mark {
      width: 52px;
      height: 52px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      background: linear-gradient(135deg, #6b46c1, #a855f7 50%, #f97316);
      box-shadow: 0 8px 20px -4px rgba(107, 70, 193, 0.4);
      margin-bottom: 16px;
    }
    .hero-title {
      font-size: 19px;
      font-weight: 700;
      color: #1f2937;
      margin: 0 0 6px 0;
    }
    .hero-subtitle {
      font-size: 13px;
      color: #6b7280;
      line-height: 1.5;
      margin: 0 0 22px 0;
    }
    .hero-cards {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
      width: 100%;
    }
    .hero-card {
      display: flex;
      align-items: center;
      gap: 8px;
      text-align: left;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 10px 12px;
      font-size: 12.5px;
      color: #374151;
      cursor: pointer;
      transition: all 0.18s cubic-bezier(0.2, 0, 0, 1);
      line-height: 1.35;
    }
    .hero-card i {
      flex-shrink: 0;
      font-size: 15px;
      color: #6b46c1;
    }
    .hero-card:hover {
      border-color: #c4b5fd;
      background: #faf9ff;
      transform: translateY(-2px);
      box-shadow: 0 6px 16px -4px rgba(107, 70, 193, 0.18);
    }
    @media (max-width: 480px) {
      .hero-cards { grid-template-columns: 1fr; }
      .hero { padding-top: 16px; }
    }

    /* ---- Assistant avatar ---- */
    .msg-avatar {
      flex-shrink: 0;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      background: linear-gradient(135deg, #6b46c1, #a855f7 50%, #f97316);
      margin-right: 8px;
      margin-top: 2px;
    }
    .msg-avatar-hidden { visibility: hidden; }

    /* ---- Bubble depth ---- */
    .bubble-assistant {
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    }
    .bubble-user {
      box-shadow: 0 2px 8px -2px rgba(107, 70, 193, 0.35);
    }

    /* ---- Advanced settings panel ---- */
    .settings-panel {
      margin-top: 8px;
      padding: 12px 14px;
      background: #fff;
      border: 1px solid #eceef1;
      border-radius: 14px;
      box-shadow: 0 4px 16px -6px rgba(15, 23, 42, 0.08);
      font-size: 12.5px;
    }
    .settings-row {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .settings-row-divided {
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px solid #f1f1f3;
    }
    .settings-label {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 0;
      color: #4b5563;
      font-weight: 500;
      white-space: nowrap;
    }
    .settings-badge {
      font-size: 11px;
      font-weight: 600;
      color: #6b46c1;
      background: #f5f3ff;
      padding: 2px 8px;
      border-radius: 8px;
      min-width: 20px;
      text-align: center;
    }
    `
  ],
})
export class ChatComponent implements AfterViewInit, OnInit {
  messages = signal<ChatMessage[]>([]);
  isSending = signal<boolean>(false);
  error = signal<string>('');
  // Progress tracking for real-time UI
  progressSteps = signal<ProgressStep[]>([]);
  showProgress = signal<boolean>(false);
  draft = '';
  // Attachments state
  attachedImages: { name: string; dataUrl: string }[] = [];
  imageUrls: string[] = [];
  newImageUrl = '';
  // Persist VLM usage across this chat session
  useVlm = false;
  // Store last request for retry functionality
  private lastRequest: {
    message: string;
    images: { name: string; dataUrl: string }[];
    imageUrls: string[];
  } | null = null;
  // Signal to track if regenerate is available
  canRegenerateSignal = signal<boolean>(false);
  // Advanced settings
  showAdvancedSettings = signal<boolean>(false);
  // Max turns in context (1 turn = 1 user + 1 assistant message), min 4, max 10, default 6
  maxTurns = signal<number>(6);
  // Max images in context (across all active messages)
  readonly maxContextImages = 10;
  // Drag & drop state
  isDraggingFiles = signal<boolean>(false);
  private dragCounter = 0;
  // Feature flag: image upload enabled (from FastAPI /api/config)
  imageUploadEnabled = signal<boolean>(false);
  // Feature flag: restrict search to healthcare domains only (default: false = all websites)
  restrictDomains = signal<boolean>(false);
  // Mode: healthcare adds privacy/scope gates; general uses research without those gates
  chatMode = signal<'healthcare' | 'general'>('healthcare');
  copiedIndex = signal<number | null>(null);
  isSuggestionsCollapsed = signal<boolean>(false);
  // AI-generated follow-up suggestions
  followUpQuestions = signal<string[]>([]);
  isLoadingFollowUp = signal<boolean>(false);
  // Mobile: collapse progress steps to show only last one
  isProgressExpanded = signal<boolean>(false);
  private sanitizer = inject(DomSanitizer);
  @ViewChild('messagesContainer') private messagesContainerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('progressStepsList') private progressStepsListRef?: ElementRef<HTMLDivElement>;
  
  // Auto-scroll progress steps list when new steps are added
  private progressScrollEffect = effect(() => {
    const steps = this.progressSteps();
    // Whenever progressSteps changes, scroll the list to bottom
    if (steps.length > 0 && this.progressStepsListRef?.nativeElement) {
      // Use setTimeout to ensure DOM is updated before scrolling
      setTimeout(() => {
        const el = this.progressStepsListRef?.nativeElement;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      }, 0);
    }
  });
  
  // Auto-scroll control: ONLY scroll when user submits new message
  private shouldScrollOnNewMessage = false;

  // Parse sources from legacy knowledge base XML into this message's source map.
  // Handles both single ID and array ID formats, plus <sources> section for titles
  parseSourcesFromContent(
    content: string,
    messageSources: CitationSourceMap = {}
  ): CitationSourceMap {
    const citationSources = { ...messageSources };

    // First, try to parse individual <source> tags (best source of titles)
    // Format: <source id="600000" url="https://..." title="Page Title" />
    const sourceTagRegex = /<source\s+id="(\d+)"\s+url="([^"]+)"\s+title="([^"]+)"\s*\/>/g;
    let match;
    while ((match = sourceTagRegex.exec(content)) !== null) {
      const id = parseInt(match[1], 10);
      const url = match[2];
      const title = match[3].replace(/&quot;/g, '"');
      const safeUrl = this.safeHttpUrl(url);
      if (safeUrl) citationSources[String(id)] = { url: safeUrl, title };
    }
    
    // Fallback: Pattern for single ID format: id="600000" url="https://..."
    const singleIdRegex = /<internal_knowledge_base\s+id="(\d+)"\s+url="([^"]+)"[^>]*>/g;
    while ((match = singleIdRegex.exec(content)) !== null) {
      const id = parseInt(match[1], 10);
      if (citationSources[String(id)]) continue; // Already parsed from <source> tags
      const url = match[2];
      const safeUrl = this.safeHttpUrl(url);
      if (!safeUrl) continue;
      const titleMatch = content.slice(match.index).match(/###\s+([^\n]+)/);
      const title = titleMatch ? titleMatch[1].trim() : `Source ${id}`;
      citationSources[String(id)] = { url: safeUrl, title };
    }
    
    // Fallback: Pattern for array ID format without <source> tags
    const arrayIdRegex = /<internal_knowledge_base\s+id="\[([^\]]+)\]"\s+url="\[([^\]]+)\]"[^>]*>/g;
    while ((match = arrayIdRegex.exec(content)) !== null) {
      const idsStr = match[1];
      const urlsStr = match[2];
      
      // Parse IDs (comma-separated numbers)
      const ids = idsStr.split(',').map((s: string) => parseInt(s.trim(), 10));
      
      // Skip if all IDs already parsed from <source> tags
      if (ids.every((id: number) => citationSources[String(id)])) continue;
      
      // Parse URLs (comma-separated quoted strings)
      const urlMatches = urlsStr.match(/"([^"]+)"/g) || [];
      const urls = urlMatches.map((u: string) => u.replace(/"/g, ''));
      
      // Map each ID to its URL (only if not already set)
      ids.forEach((id: number, index: number) => {
        if (citationSources[String(id)]) return;
        const url = urls[index] || urls[0] || '#';
        const safeUrl = this.safeHttpUrl(url);
        if (safeUrl) {
          citationSources[String(id)] = { url: safeUrl, title: `Source ${id}` };
        }
      });
    }

    return citationSources;
  }

  renderMarkdown(input: string, messageSources: CitationSourceMap = {}): string {
    // Parse sources from internal knowledge base tags
    const citationSources = this.parseSourcesFromContent(input, messageSources);
    
    // Configure marked to open links in new tab
    const renderer = new marked.Renderer();
    renderer.link = (href: string, title: string | null, text: string) => {
      const safeHref = this.safeHttpUrl(href);
      if (!safeHref) return text;
      const titleAttr = title ? ` title="${this.escapeHtml(title)}"` : '';
      return `<a href="${this.escapeHtml(safeHref)}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
    };
    
    // Remove internal knowledge base tags from output (they're for context only)
    let cleanedInput = (input ?? '').replace(/<internal_knowledge_base[^>]*>[\s\S]*?<\/internal_knowledge_base>/g, '');
    
    // Remove any stray SVG tags from LLM output (except our citation icons)
    cleanedInput = cleanedInput.replace(/<svg[^>]*>[\s\S]*?<\/svg>/gi, '');
    
    // First pass markdown
    let rawHtml = marked.parse(cleanedInput, { renderer }) as string;
    
    // Convert <cite>[1]</cite> or <cite>[1,2,3]</cite> to citation badges (after markdown)
    // Handle HTML-escaped version
    rawHtml = rawHtml.replace(/&lt;cite&gt;\[([^\]]+)\]&lt;\/cite&gt;/g, () => '');
    rawHtml = rawHtml.replace(/<cite>\[([^\]]+)\]<\/cite>/g, () => '');
    
    // Handle markdown-style links: [Source N](url) - convert to Omega-style badges
    // Match any <a> tag with "Source" followed by a number in its text
    rawHtml = rawHtml.replace(/<a\s+([^>]*)>([^<]*)<\/a>/gi, (match, attrs, text) => {
      // Check if text contains "Source" followed by a number
      const sourceMatch = text.match(/[Ss]ource\s*(\d+)/);
      if (!sourceMatch) return match; // Not a source link, keep original
      
      // Extract href from attributes
      const hrefMatch = attrs.match(/href="([^"]*)"/);
      if (!hrefMatch) return match; // No href, keep original
      
      const url = hrefMatch[1];
      const num = sourceMatch[1];
      return '';
    });
    
    return this.sanitizer.sanitize(SecurityContext.HTML, rawHtml) || '';
  }

  // Parse domain name dynamically: "info.hapusa.com" → "Info Hapusa.com"
  private parseDomainName(urlString: string): string {
    // Remove protocol (http://, https://, ftp://, etc.)
    let hostname = urlString.replace(/^[a-zA-Z]+:\/\//, '');
    
    // Remove www. prefix
    hostname = hostname.replace(/^www\./, '');
    
    // Remove path, query params, hash (everything after first / ? or #)
    hostname = hostname.split(/[/?#]/)[0];
    
    // Split by dots
    const parts = hostname.split('.');
    
    if (parts.length < 2) {
      return hostname.charAt(0).toUpperCase() + hostname.slice(1);
    }
    
    // Get TLD (last part) and name parts (everything before TLD)
    const tld = parts[parts.length - 1];
    const nameParts = parts.slice(0, -1);
    
    // Capitalize each name part, join with space, add .tld
    const name = nameParts.map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ');
    return `${name}.${tld}`;
  }

  // Build citation badges with purple theme
  private buildCitationBadges(ids: string, citationSources: CitationSourceMap): string {
    const idList = ids
      .split(',')
      .map((id: string) => id.trim())
      .filter((id: string) => /^\d+$/.test(id));
    if (idList.length === 0) return '';
    
    // Get first source for display
    const firstId = parseInt(idList[0], 10);
    const firstSource = citationSources[String(firstId)];
    const fullTitle = firstSource?.title || `Source ${idList[0]}`;
    
    // Extract domain name from URL dynamically
    let displayName = fullTitle;
    try {
      if (firstSource?.url) {
        displayName = this.parseDomainName(firstSource.url);
      }
    } catch {
      displayName = fullTitle.length > 12 ? fullTitle.slice(0, 10) + '...' : fullTitle;
    }
    
    // Build badge text: "DomainName" or "DomainName +N"
    const extraCount = idList.length > 1 ? ` +${idList.length - 1}` : '';
    const escapedTitle = this.escapeHtml(fullTitle);
    const escapedLabel = this.escapeHtml(`${displayName}${extraCount}`);
    
    const safeUrl = this.safeHttpUrl(firstSource?.url || '');
    if (!safeUrl) {
      return `<span class="cite-badge" title="${escapedTitle}">${escapedLabel}</span>`;
    }
    return `<a class="cite-badge" href="${this.escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer" title="${escapedTitle}">${escapedLabel}</a>`;
  }

  // Build badge from markdown link [Source N](url)
  private buildSourceBadge(url: string, num: string): string {
    const safeUrl = this.safeHttpUrl(url);
    if (!safeUrl) return `<span class="cite-badge">Source ${this.escapeHtml(num)}</span>`;
    let displayName = `Source ${num}`;
    
    try {
      displayName = this.parseDomainName(safeUrl);
    } catch {
      // Keep default
    }
    
    return `<a class="cite-badge" href="${this.escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer" title="${this.escapeHtml(safeUrl)}">${this.escapeHtml(displayName)}</a>`;
  }

  private safeHttpUrl(value: string): string | null {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
    } catch {
      return null;
    }
  }
  
  // Helper to escape HTML special characters
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
    // Listen for user scroll to detect when they scroll up
    this.setupScrollListener();
  }

  private setupScrollListener(): void {
    // No scroll listener needed - we only scroll on new message submit
  }

  ngOnInit(): void {
    if (this.messages().length === 0) {
      this.messages.set([
        {
          role: 'assistant',
          content: 'Welcome to Civie - AI-Powered Solutions for Unmatched Radiology Operations Advantage. How can I help you today?',
          timestamp: Date.now(),
        },
      ]);
    }
    // Fetch feature flags from server
    this.loadConfig();
  }

  private async loadConfig(): Promise<void> {
    try {
      const response = await apiFetch('/api/config');
      if (response.ok) {
        const config = await response.json();
        this.imageUploadEnabled.set(config.enableImageUpload === true);
        this.restrictDomains.set(config.restrictHealthcareDomains === true);
      }
    } catch (err) {
      console.warn('Failed to load config, using defaults:', err);
    }
  }

  // Toggle domain restriction setting
  toggleRestrictDomains(): void {
    this.restrictDomains.set(!this.restrictDomains());
  }

  // Toggle chat mode between healthcare and general
  setChatMode(mode: 'healthcare' | 'general'): void {
    // Reset all states first - stop any in-progress requests
    this.isSending.set(false);
    this.error.set('');
    this.progressSteps.set([]);
    this.showProgress.set(false);
    this.canRegenerateSignal.set(false);
    this.followUpQuestions.set([]);
    this.lastRequest = null;
    this.draft = '';
    this.attachedImages = [];
    this.imageUrls = [];
    
    // Set new mode
    this.chatMode.set(mode);
    
    // Clear all messages and reset to welcome message
    const welcomeMsg = mode === 'general'
      ? "Hello! I'm Civie-AI. How can I help you today?"
      : "Hello! I'm Civie-AI, here to help you with healthcare-related questions, radiology, and Civie products. How can I assist you today?";
    this.messages.set([
      { role: 'assistant', content: welcomeMsg, timestamp: Date.now() }
    ]);
  }

  private scrollToBottom(): void {
    const el = this.messagesContainerRef?.nativeElement;
    if (!el) return;
    const doScroll = () => { el.scrollTop = el.scrollHeight; };
    // Ensure we scroll after the DOM has painted (and possibly reflowed)
    requestAnimationFrame(() => {
      doScroll();
      requestAnimationFrame(doScroll);
      setTimeout(doScroll, 0);
    });
  }

  private scheduleScrollToBottom(): void {
    this.scrollToBottom();
  }

  onEnterKey(event: Event): void {
    const kb = event as KeyboardEvent;
    if (kb.shiftKey) return; // allow Shift+Enter for newline
    event.preventDefault();
    // trigger form submit
    this.onSend(new Event('submit'));
  }

  quickFill(text: string): void {
    this.draft = text;
    this.isSuggestionsCollapsed.set(true);
    // Auto-submit after filling
    setTimeout(() => this.onSend(new Event('submit')), 50);
  }

  sendEnabled(): boolean {
    return !this.isSending() && (this.draft.trim().length > 0 || this.attachedImages.length > 0 || this.imageUrls.length > 0);
  }

  onFilesSelected(event: Event): void {
    if (!this.imageUploadEnabled()) return;
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    const remaining = this.getRemainingImageSlots();
    
    // Check if we can add any images
    if (remaining <= 0) {
      this.error.set(`Maximum ${this.maxContextImages} images allowed in active context. Remove some images or wait for older messages to leave context.`);
      input.value = '';
      return;
    }
    
    // Limit files to remaining slots
    const filesToProcess = files.slice(0, remaining);
    const skipped = files.length - filesToProcess.length;
    
    for (const file of filesToProcess) {
      if (!file.type.startsWith('image/')) continue;
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        this.attachedImages.push({ name: file.name, dataUrl: result });
      };
      reader.readAsDataURL(file);
    }
    
    // Show warning if some files were skipped
    if (skipped > 0) {
      this.error.set(`Only ${filesToProcess.length} image(s) added. ${skipped} skipped due to ${this.maxContextImages} image limit.`);
    }
    
    // reset input to allow re-selecting the same files
    input.value = '';
  }

  removeImage(idx: number): void {
    this.attachedImages.splice(idx, 1);
  }

  addImageUrl(): void {
    const url = (this.newImageUrl || '').trim();
    if (!url) return;
    this.imageUrls.push(url);
    this.newImageUrl = '';
  }

  removeImageUrl(idx: number): void {
    this.imageUrls.splice(idx, 1);
  }

  // Display helpers for attachment summaries in user bubbles
  formatAttachmentSummary(attList?: { src: string; name?: string }[]): string {
    const attachments = Array.isArray(attList) ? attList : [];
    if (attachments.length === 0) return '';
    const count = attachments.length;
    const label = count === 1 ? 'image' : 'images';
    let totalBytes = 0;
    for (const att of attachments) {
      const src = att?.src || '';
      if (src.startsWith('data:')) {
        const commaIdx = src.indexOf(',');
        if (commaIdx > -1) {
          const b64 = src.slice(commaIdx + 1);
          const padding = (b64.match(/=+$/) || [''])[0].length;
          const len = b64.length - padding;
          const bytes = Math.floor((len * 3) / 4);
          totalBytes += bytes;
        }
      }
    }
    const sizePart = totalBytes > 0 ? ` • Total: ${this.formatBytes(totalBytes)}` : '';
    return `${count} ${label}${sizePart}`;
  }

  private formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let idx = 0;
    let value = bytes;
    while (value >= 1024 && idx < units.length - 1) {
      value = value / 1024;
      idx++;
    }
    const fixed = value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2);
    return `${fixed}${units[idx]}`;
  }

  placeholderText(): string {
    const count = this.attachedImages.length + this.imageUrls.length;
    if (count > 0) {
      return `Ask a question about your ${count} images...`;
    }
    return 'Type your message...';
  }

  // Drag & drop handlers
  private eventHasFiles(e: DragEvent): boolean {
    const types = Array.from(e.dataTransfer?.types || []);
    return types.includes('Files');
  }

  handleDragOver(event: DragEvent): void {
    if (!this.imageUploadEnabled() || !this.eventHasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFiles.set(true);
  }

  handleDragEnter(event: DragEvent): void {
    if (!this.imageUploadEnabled() || !this.eventHasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.isDraggingFiles.set(true);
  }

  handleDragLeave(event: DragEvent): void {
    if (!this.imageUploadEnabled() || !this.eventHasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = Math.max(0, this.dragCounter - 1);
    if (this.dragCounter === 0) this.isDraggingFiles.set(false);
  }

  async handleDrop(event: DragEvent): Promise<void> {
    if (!this.imageUploadEnabled() || !this.eventHasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    
    const files = Array.from(event.dataTransfer?.files || []);
    const remaining = this.getRemainingImageSlots();
    
    // Check if we can add any images
    if (remaining <= 0) {
      this.error.set(`Maximum ${this.maxContextImages} images allowed in active context.`);
      this.dragCounter = 0;
      this.isDraggingFiles.set(false);
      return;
    }
    
    // Limit files to remaining slots
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const filesToProcess = imageFiles.slice(0, remaining);
    const skipped = imageFiles.length - filesToProcess.length;
    
    for (const file of filesToProcess) {
      const reader = new FileReader();
      const dataUrl: string = await new Promise(resolve => {
        reader.onload = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(file);
      });
      this.attachedImages.push({ name: file.name, dataUrl });
    }
    
    // Show warning if some files were skipped
    if (skipped > 0) {
      this.error.set(`Only ${filesToProcess.length} image(s) added. ${skipped} skipped due to ${this.maxContextImages} image limit.`);
    }
    
    this.dragCounter = 0;
    this.isDraggingFiles.set(false);
  }

  async copyMessage(text: string, index?: number): Promise<void> {
    try {
      if ((navigator as any)?.clipboard?.writeText) {
        await (navigator as any).clipboard.writeText(text);
      } else {
        console.warn('Clipboard API not available; copy not supported in this context.');
      }
    } catch (err) {
      console.error('Copy failed:', err);
    } finally {
      if (typeof index === 'number') {
        this.copiedIndex.set(index);
        setTimeout(() => {
          if (this.copiedIndex() === index) this.copiedIndex.set(null);
        }, 1500);
      }
    }
  }

  getTranscriptText(): string {
    const items = this.messages();
    const formatTime = (ms?: number) => {
      const d = new Date(ms ?? Date.now());
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    };
    const labelFor = (role: ChatMessage['role']) => {
      return role === 'user' ? 'You' : role === 'assistant' ? 'Assistant' : 'System';
    };
    return items
      .map(m => `${labelFor(m.role)} (${formatTime(m.timestamp)}):\n${m.content}`)
      .join('\n\n');
  }

  // Reset chat to start a new conversation
  resetChat(): void {
    // Clear all messages except the welcome message
    this.messages.set([{
      role: 'assistant',
      content: 'Welcome to Civie - AI-Powered Solutions for Unmatched Radiology Operations Advantage. How can I help you today?',
      timestamp: Date.now(),
    }]);
    // Reset all state
    this.error.set('');
    this.isSending.set(false);
    this.draft = '';
    this.resetProgress();
    // Clear follow-up questions
    this.followUpQuestions.set([]);
    // Clear any attached images
    this.attachedImages = [];
    this.useVlm = false;
    this.lastRequest = null;
    this.canRegenerateSignal.set(false);
  }

  // Check if we can regenerate (has last request and last message is assistant with content)
  canRegenerate(): boolean {
    return this.canRegenerateSignal();
  }
  
  // Update regenerate availability
  private updateCanRegenerate(): void {
    if (!this.lastRequest) {
      this.canRegenerateSignal.set(false);
      return;
    }
    const msgs = this.messages();
    if (msgs.length < 2) {
      this.canRegenerateSignal.set(false);
      return;
    }
    const lastMsg = msgs[msgs.length - 1];
    const canRegen = lastMsg.role === 'assistant' && !!lastMsg.content && lastMsg.content.length > 10;
    this.canRegenerateSignal.set(canRegen);
  }

  // Toggle suggestions collapse state
  toggleSuggestions(): void {
    this.isSuggestionsCollapsed.set(!this.isSuggestionsCollapsed());
  }

  // Toggle progress steps expansion (mobile)
  toggleProgressExpansion(): void {
    this.isProgressExpanded.set(!this.isProgressExpanded());
  }

  // Toggle plan expansion for agent_thinking steps
  togglePlanExpansion(step: ProgressStep): void {
    step.expanded = !step.expanded;
  }

  // Show the current step, never an earlier leftover such as the initial "Starting" event.
  getLastProgressStep(): ProgressStep | null {
    const steps = this.progressSteps();
    if (steps.length === 0) return null;

    const latest = (predicate: (step: ProgressStep) => boolean): ProgressStep | undefined =>
      [...steps].reverse().find(predicate);

    const runningWithPages = latest(
      (step) => step.status === 'running' && !!step.detail?.totalPages
    );
    if (runningWithPages) return runningWithPages;

    const withPages = latest((step) => !!step.detail?.totalPages && step.status !== 'done');
    if (withPages) return withPages;

    // The opening "request" event stays running until later steps arrive. Ignore it once
    // research or generation has started, otherwise the pill snaps back to "Starting".
    const running = latest((step) => step.status === 'running' && step.step !== 'request');
    if (running) return running;

    return latest((step) => step.step !== 'request') ?? steps[steps.length - 1];
  }

  // Toggle advanced settings panel
  toggleAdvancedSettings(): void {
    this.showAdvancedSettings.set(!this.showAdvancedSettings());
  }

  // Count images for a specific turns value (to validate before changing)
  getImagesForTurns(turns: number): number {
    const msgs = this.messages();
    const maxMessages = turns * 2;
    
    let contextMessages: ChatMessage[];
    if (msgs.length <= maxMessages + 1) {
      contextMessages = msgs;
    } else {
      const welcome = msgs[0];
      const recentMessages = msgs.slice(-(maxMessages));
      contextMessages = [welcome, ...recentMessages];
    }
    
    let imageCount = 0;
    for (const m of contextMessages) {
      if (m.attachments && m.attachments.length > 0) {
        imageCount += m.attachments.length;
      }
    }
    // Also count pending attachments
    return imageCount + this.attachedImages.length + this.imageUrls.length;
  }

  // Calculate max allowed turns based on image limit
  getMaxAllowedTurns(): number {
    // Find the highest turns value that doesn't exceed image limit
    for (let t = 10; t >= 4; t--) {
      if (this.getImagesForTurns(t) <= this.maxContextImages) {
        return t;
      }
    }
    return 4; // Minimum
  }

  // Update max turns (min 4, max 10) - validates image limit
  updateMaxTurns(value: number): void {
    const clamped = Math.max(4, Math.min(10, value));
    
    // Check if new turns would exceed image limit
    const imagesAtNewTurns = this.getImagesForTurns(clamped);
    if (imagesAtNewTurns > this.maxContextImages) {
      // Find the maximum allowed turns
      const maxAllowed = this.getMaxAllowedTurns();
      this.error.set(`Cannot set ${clamped} turns: would include ${imagesAtNewTurns} images (max ${this.maxContextImages}). Maximum allowed: ${maxAllowed} turns.`);
      // Set to max allowed instead
      this.maxTurns.set(Math.min(clamped, maxAllowed));
      return;
    }
    
    this.error.set(''); // Clear any previous error
    this.maxTurns.set(clamped);
  }

  // Check if a message is within the context window (will be sent to API)
  // Returns true if the message is in context, false if it's outside (should be faded)
  isMessageInContext(index: number): boolean {
    const msgs = this.messages();
    const maxMessages = this.maxTurns() * 2; // Each turn = 2 messages (user + assistant)
    
    // Always include the first message (welcome)
    if (index === 0) return true;
    
    // Calculate how many messages from the end are in context
    const totalMessages = msgs.length;
    const contextStartIndex = Math.max(1, totalMessages - maxMessages); // Start after welcome
    
    return index >= contextStartIndex;
  }

  // True only while nothing but the opening welcome message exists - drives the
  // empty-state hero screen instead of rendering that message as a plain bubble.
  isWelcomeOnly(): boolean {
    return this.messages().length === 1;
  }

  // True when index i starts a new run of consecutive same-role messages. Used to show
  // the assistant avatar once per run instead of once per message.
  isRunStart(i: number): boolean {
    const msgs = this.messages();
    return i === 0 || msgs[i - 1]?.role !== msgs[i].role;
  }

  // True when index i ends a run of consecutive same-role messages. Used to show the
  // timestamp once per run (at its end) instead of cluttering every bubble.
  isRunEnd(i: number): boolean {
    const msgs = this.messages();
    return i === msgs.length - 1 || msgs[i + 1]?.role !== msgs[i].role;
  }

  // Count images in active context (messages that will be sent to API)
  getImagesInContext(): number {
    const msgs = this.messages();
    const maxMessages = this.maxTurns() * 2;
    
    // Get messages in context (same logic as isMessageInContext)
    let contextMessages: ChatMessage[];
    if (msgs.length <= maxMessages + 1) {
      contextMessages = msgs;
    } else {
      const welcome = msgs[0];
      const recentMessages = msgs.slice(-(maxMessages));
      contextMessages = [welcome, ...recentMessages];
    }
    
    // Count images in context messages
    let imageCount = 0;
    for (const m of contextMessages) {
      if (m.attachments && m.attachments.length > 0) {
        imageCount += m.attachments.length;
      }
    }
    return imageCount;
  }

  // Get remaining image slots available
  getRemainingImageSlots(): number {
    const usedInContext = this.getImagesInContext();
    const pendingToAttach = this.attachedImages.length + this.imageUrls.length;
    return Math.max(0, this.maxContextImages - usedInContext - pendingToAttach);
  }

  // Check if we can add more images
  canAddMoreImages(): boolean {
    return this.getRemainingImageSlots() > 0;
  }

  // Get total images (context + pending attachments)
  getTotalImagesCount(): number {
    return this.getImagesInContext() + this.attachedImages.length + this.imageUrls.length;
  }

  // Get messages to send to API (limited by maxTurns) - includes image attachments
  getMessagesForApi(allMessages: ChatMessage[]): any[] {
    const maxMessages = this.maxTurns() * 2;
    
    // Helper to convert message to API format (with attachments as image_url parts)
    const toApiMessage = (m: ChatMessage) => {
      if (m.attachments && m.attachments.length > 0) {
        const contentParts: any[] = [];
        if (m.content) contentParts.push({ type: 'text', text: m.content });
        for (const att of m.attachments) {
          contentParts.push({ type: 'image_url', image_url: { url: att.src } });
        }
        return { role: m.role, content: contentParts };
      }
      return { role: m.role, content: m.content };
    };
    
    // Always include first message (welcome), then last N messages
    if (allMessages.length <= maxMessages + 1) {
      return allMessages.map(toApiMessage);
    }
    
    // Keep welcome + last N messages
    const welcome = allMessages[0];
    const recentMessages = allMessages.slice(-(maxMessages));
    
    return [welcome, ...recentMessages].map(toApiMessage);
  }

  // Regenerate/Retry - reuses onSend with stored request data
  // This ensures EXACT same flow as normal submit
  async regenerateResponse(): Promise<void> {
    await this.retryLastRequest();
  }

  async retryLastRequest(): Promise<void> {
    if (!this.lastRequest || this.isSending()) return;
    
    const currentMessages = this.messages();
    
    // Remove the last assistant message (failed or successful)
    if (currentMessages.length > 0 && currentMessages[currentMessages.length - 1].role === 'assistant') {
      this.messages.set(currentMessages.slice(0, -1));
    }
    
    // Also remove the last user message since onSend will add it again
    const afterRemovingAssistant = this.messages();
    if (afterRemovingAssistant.length > 0 && afterRemovingAssistant[afterRemovingAssistant.length - 1].role === 'user') {
      this.messages.set(afterRemovingAssistant.slice(0, -1));
    }
    
    // Restore the draft and attachments from lastRequest
    this.draft = this.lastRequest.message;
    this.attachedImages = [...this.lastRequest.images];
    this.imageUrls = [...this.lastRequest.imageUrls];
    
    // Clear error
    this.error.set('');
    
    // Call onSend - this will add user message, add assistant placeholder, and make API call
    // Exact same flow as normal submit button
    await this.onSend(new Event('submit'));
  }

  // Get display label for a progress step (mode-aware)
  getStepLabel(step: string): string {
    const labels = this.chatMode() === 'general' ? STEP_LABELS_GENERAL : STEP_LABELS_HEALTHCARE;
    return labels[step] || STEP_LABELS_HEALTHCARE[step] || step;
  }

  // Get iteration label based on iteration number (AGI-like progressive labels)
  getIterationLabel(iteration: number): string {
    const iterNum = iteration - 1; // iteration 2 = #1, iteration 3 = #2, etc.
    switch (iterNum) {
      case 1: return 'Rethinking';
      case 2: return 'Re-analyzing';
      case 3: return 'Deep Reasoning';
      case 4: return 'Synthesizing';
      default: return iterNum > 4 ? 'Final Analysis' : 'Processing';
    }
  }

  // Update progress step in the list
  private updateProgress(step: string, status: ProgressStep['status'], message: string, detail?: ProgressStep['detail']): void {
    // DEBUG: Log what we receive for agent_analysis
    if (step === 'agent_analysis') {
      console.log('[DEBUG] agent_analysis received:', { step, status, message, detail });
      console.log('[DEBUG] planMarkdown:', detail?.planMarkdown?.slice(0, 200));
    }
    
    // Hide classification step from UI as requested
    if (step === 'classify') return;

    const current = this.progressSteps();
    
    // For agent_analysis, ALWAYS add as new entry (don't replace)
    // This allows multiple analysis steps to be shown
    const shouldAlwaysAdd = step === 'agent_analysis';
    const existingIndex = shouldAlwaysAdd ? -1 : current.findIndex(p => p.step === step);
    
    // ALWAYS use our clean messages - never show backend technical messages
    // Once a message is assigned to a step, keep it (just change icon for done)
    let displayMessage: string;
    if (existingIndex >= 0 && current[existingIndex].message) {
      // Step already exists - keep the SAME message, only icon changes
      displayMessage = current[existingIndex].message;
    } else if (RUNNING_MESSAGES[step]) {
      // New step - assign a random message (this message stays with this step)
      displayMessage = getRandomMessage(RUNNING_MESSAGES[step]);
    } else {
      displayMessage = '';
    }

    const newStep: ProgressStep = { step, status, message: displayMessage, detail };
    
    let updated: ProgressStep[];
    if (existingIndex >= 0) {
      updated = [...current];
      updated[existingIndex] = newStep;
    } else {
      updated = [...current, newStep];
    }

    // Only one step should look current. Leaving "request" running makes the pill
    // jump back to "Starting" after search/research finishes.
    if (status === 'running') {
      updated = updated.map((item) =>
        item.step === step || item.status !== 'running' ? item : { ...item, status: 'done' }
      );
    }
    
    // Sort steps: done/error at top, running at bottom (maintains order within each group)
    updated.sort((a, b) => {
      const statusOrder: Record<string, number> = { done: 0, error: 1, pending: 2, running: 3 };
      return (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
    });
    
    this.progressSteps.set(updated);
  }

  // Reset progress for new request
  private resetProgress(): void {
    this.progressSteps.set([]);
    this.showProgress.set(false);
  }

  async onSend(event: Event): Promise<void> {
    event.preventDefault();
    const trimmed = this.draft.trim();
    if (this.isSending()) return;
    if (!trimmed && this.attachedImages.length === 0 && this.imageUrls.length === 0) return;
    
    // Scroll to bottom when user sends a new message
    this.shouldScrollOnNewMessage = true;
    this.isSuggestionsCollapsed.set(true);

    this.error.set('');
    this.isSending.set(true);
    this.resetProgress();
    // Clear follow-up questions when starting a new message
    this.followUpQuestions.set([]);
    // Citation sources remain attached to their individual assistant messages.

    // Capture current attachments immediately so we can clear the UI right away
    const imagesToSend = [...this.attachedImages];
    const urlsToSend = [...this.imageUrls];
    if (imagesToSend.length > 0 || urlsToSend.length > 0) {
      this.useVlm = true;
    }
    
    // Store request for retry functionality
    this.lastRequest = {
      message: trimmed,
      images: [...imagesToSend],
      imageUrls: [...urlsToSend],
    };
    const attachments: { src: string; name?: string }[] = [
      ...imagesToSend.map(img => ({ src: img.dataUrl, name: img.name })),
      ...urlsToSend.map(url => ({ src: url }))
    ];
    const nextMessages: ChatMessage[] = [
      ...this.messages(),
      { role: 'user', content: trimmed, timestamp: Date.now(), attachments }
    ];
    this.messages.set(nextMessages);
    this.scrollToBottom();
    this.draft = '';
    // Clear composer attachments immediately after enqueueing the user message
    this.attachedImages = [];
    this.imageUrls = [];

    try {
      // Append a placeholder assistant message immediately and stream into it
      const assistantIndex = this.messages().length;
      this.messages.set([
        ...this.messages(),
        { role: 'assistant', content: '', timestamp: Date.now() }
      ]);
      // Reset scroll flag - no auto-scroll during streaming
      this.shouldScrollOnNewMessage = false;

      // Stream from the local proxy at /api/chat
      // Build content parts for the last user message
      const parts: any[] = [];
      if (trimmed) parts.push({ type: 'text', text: trimmed });
      for (const img of imagesToSend) {
        // Use Omega-style image part: image_url must be an object with { url }
        parts.push({ type: 'image_url', image_url: { url: img.dataUrl } });
      }
      for (const url of urlsToSend) {
        // Both data: URLs and http(s) links use the same shape
        parts.push({ type: 'image_url', image_url: { url } });
      }

      // Compose messages for API with turn limit
      // Get limited messages (welcome + last N turns)
      const maxMessages = this.maxTurns() * 2;
      let limitedMessages: ChatMessage[];
      
      if (nextMessages.length <= maxMessages + 1) {
        // All messages fit within limit
        limitedMessages = nextMessages;
      } else {
        // Keep welcome + last N messages (excluding the new user message we just added)
        const welcome = nextMessages[0];
        const recentMessages = nextMessages.slice(-(maxMessages));
        limitedMessages = [welcome, ...recentMessages];
      }
      
      // Build base messages (all except last user message) - include images from attachments
      const baseMessages = limitedMessages.slice(0, -1).map(m => {
        // If message has attachments (images), build content as array with text + images
        if (m.attachments && m.attachments.length > 0) {
          const contentParts: any[] = [];
          if (m.content) contentParts.push({ type: 'text', text: m.content });
          for (const att of m.attachments) {
            contentParts.push({ type: 'image_url', image_url: { url: att.src } });
          }
          return { role: m.role, content: contentParts };
        }
        return { role: m.role, content: m.content };
      });
      const finalMessages = parts.length > 0
        ? [...baseMessages, { role: 'user', content: parts }]
        : baseMessages.concat([{ role: 'user', content: limitedMessages[limitedMessages.length - 1].content }]);
      
      const response = await apiFetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          temperature: 0.7,
          messages: finalMessages,
          sse: true, // Enable SSE for progress events
          vlm: this.useVlm,
          restrictDomains: this.restrictDomains(), // User preference for domain restriction
          mode: this.chatMode(), // 'healthcare' or 'general'
        }),
      });

      if (!response.ok || !response.body) {
        const contentType = response.headers.get('content-type') || '';
        let human = 'Request failed';
        try {
          const text = await response.text();
          if (contentType.includes('application/json')) {
            try {
              const json = JSON.parse(text);
              human = String(json?.error || json?.message || json?.detail || text || human);
            } catch {
              human = text || human;
            }
          } else {
            try {
              const json = JSON.parse(text);
              human = String(json?.error || json?.message || json?.detail || text || human);
            } catch {
              human = text || human;
            }
          }
        } catch {}
        throw new Error(human);
      }

      // Show progress UI once we start receiving events
      this.showProgress.set(true);
      let terminalEventReceived = false;

      for await (const event of readJsonSse<ChatStreamEvent>(
        response.body,
        { requireTerminal: true }
      )) {
        try {
                if (event.type === 'progress') {
                  // Update progress UI
                  this.updateProgress(
                    event.step || 'processing',
                    event.status || 'running',
                    event.message || '',
                    event.detail
                  );
                  // Note: auto-scroll handled by effect based on user scroll position
                } else if (event.type === 'sources') {
                  // Keep citation sources on this assistant message so IDs from a
                  // later request cannot overwrite links in earlier answers.
                  if (event.sources && Array.isArray(event.sources)) {
                    const updated = [...this.messages()];
                    const sources = {
                      ...(updated[assistantIndex]?.citationSources || {}),
                    };
                    for (const source of event.sources) {
                      const safeUrl = this.safeHttpUrl(source.url);
                      if (!safeUrl) continue;
                      sources[String(source.id)] = {
                        url: safeUrl,
                        title: source.title || 'Untitled' 
                      };
                    }
                    updated[assistantIndex] = {
                      ...updated[assistantIndex],
                      citationSources: sources,
                    };
                    this.messages.set(updated);
                  }
                } else if (event.type === 'content') {
                  // Append content to assistant message
                  const updated = [...this.messages()];
                  updated[assistantIndex] = {
                    ...updated[assistantIndex],
                    content: updated[assistantIndex].content + (event.content || ''),
                  };
                  this.messages.set(updated);
                  // Note: auto-scroll handled by effect based on user scroll position
                } else if (event.type === 'revert') {
                  terminalEventReceived = true;
                  // PII detected or out of scope - revert the user message
                  const current = [...this.messages()];
                  
                  // Find and remove the last user message and assistant placeholder
                  // Remove assistant placeholder (last message if it's assistant)
                  if (current.length > 0 && current[current.length - 1]?.role === 'assistant') {
                    current.pop();
                  }
                  // Get and remove the last user message
                  let revertedMessage: ChatMessage | undefined;
                  if (current.length > 0 && current[current.length - 1]?.role === 'user') {
                    revertedMessage = current.pop();
                  }
                  
                  this.messages.set(current);
                  
                  // Restore input field with the reverted message
                  if (revertedMessage) {
                    this.draft = revertedMessage.content || '';
                    // Restore images to attachedImages (same as normal upload)
                    // Clear both arrays first to ensure clean state
                    this.attachedImages = [];
                    this.imageUrls = [];
                    if (revertedMessage.attachments && revertedMessage.attachments.length > 0) {
                      // Add to attachedImages so it looks like normal first-time upload
                      this.attachedImages = revertedMessage.attachments.map(a => ({
                        name: a.name || 'image.png',
                        dataUrl: a.src
                      }));
                    }
                  }
                  
                  // Show error message in the error signal (displayed in red box)
                  this.error.set(event.message || 'Request rejected. Please modify your message and try again.');
                  this.showProgress.set(false);
                } else if (event.type === 'done') {
                  terminalEventReceived = true;
                  // Hide progress when done
                  this.showProgress.set(false);
                } else if (event.type === 'error') {
                  terminalEventReceived = true;
                  // Restore draft and attachments before throwing error
                  if (this.lastRequest) {
                    this.draft = this.lastRequest.message || '';
                    this.attachedImages = [...(this.lastRequest.images || [])];
                    this.imageUrls = [...(this.lastRequest.imageUrls || [])];
                  }
                  // Remove failed messages
                  const errCurrent = [...this.messages()];
                  if (errCurrent.length > 0 && errCurrent[errCurrent.length - 1]?.role === 'assistant') {
                    errCurrent.pop();
                  }
                  if (errCurrent.length > 0 && errCurrent[errCurrent.length - 1]?.role === 'user') {
                    errCurrent.pop();
                  }
                  this.messages.set(errCurrent);
                  this.error.set(event.error || event.message || 'An error occurred');
                  this.showProgress.set(false);
                  this.isSending.set(false);
                  return; // Exit instead of throw to avoid double handling
                } else if (event.content !== undefined) {
                  // Legacy format support: { content, done }
                  const updated = [...this.messages()];
                  updated[assistantIndex] = {
                    ...updated[assistantIndex],
                    content: updated[assistantIndex].content + (event.content || ''),
                  };
                  this.messages.set(updated);
                  // Note: auto-scroll handled by effect based on user scroll position
                  if (event.done) {
                    terminalEventReceived = true;
                    this.showProgress.set(false);
                  }
                }
        } catch (eventError) {
          console.warn('Failed to process SSE event:', eventError);
        }
      }

      if (!terminalEventReceived) {
        throw new Error('The response stream ended before completion. Please try again.');
      }
      
      // Ensure progress is hidden when stream ends
      this.showProgress.set(false);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      this.error.set(message);
      this.showProgress.set(false);
      
      // RESTORE draft and attachments on error so user can retry
      if (this.lastRequest) {
        this.draft = this.lastRequest.message || '';
        this.attachedImages = [...(this.lastRequest.images || [])];
        this.imageUrls = [...(this.lastRequest.imageUrls || [])];
      }
      
      // Remove the failed messages (user message and assistant placeholder)
      const current = [...this.messages()];
      // Remove assistant placeholder if exists
      if (current.length > 0 && current[current.length - 1]?.role === 'assistant') {
        current.pop();
      }
      // Remove user message that failed
      if (current.length > 0 && current[current.length - 1]?.role === 'user') {
        current.pop();
      }
      this.messages.set(current);
    } finally {
      this.isSending.set(false);
      this.updateCanRegenerate();
      
      // Fetch follow-up suggestions after successful response
      const msgs = this.messages();
      if (msgs.length >= 2 && msgs[msgs.length - 1]?.role === 'assistant' && msgs[msgs.length - 1]?.content?.length > 50) {
        this.fetchFollowUpSuggestions();
      }
    }
  }

  // Fetch AI-generated follow-up suggestions
  // Controlled by ENABLE_FOLLOW_UP env variable on backend (disabled by default)
  private async fetchFollowUpSuggestions(): Promise<void> {
    // Don't show loading initially - if disabled, backend returns instantly
    // If enabled, response takes ~2s so loading will show briefly
    this.followUpQuestions.set([]);
    
    try {
      const msgs = this.messages();
      const apiMessages = this.getMessagesForApi(msgs);
      
      // Small delay before showing loading (avoids flicker when disabled)
      const loadingTimeout = setTimeout(() => this.isLoadingFollowUp.set(true), 200);
      
      const response = await apiFetch('/api/follow-up-suggestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages: apiMessages })
      });
      
      clearTimeout(loadingTimeout);
      
      if (response.ok) {
        const data = await response.json();
        // If feature is disabled on backend, silently return (no UI shown)
        if (data.disabled) {
          this.isLoadingFollowUp.set(false);
          return;
        }
        if (data.questions && Array.isArray(data.questions) && data.questions.length > 0) {
          this.followUpQuestions.set(data.questions.slice(0, 3));
        }
      }
    } catch (err) {
      // Silently fail - follow-up suggestions are optional
    } finally {
      this.isLoadingFollowUp.set(false);
    }
  }

  // Use a follow-up question
  useFollowUpQuestion(question: string): void {
    this.draft = question;
    this.followUpQuestions.set([]);
    // Auto-submit after filling
    setTimeout(() => this.onSend(new Event('submit')), 50);
  }
}


